import Big from 'big.js';

import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AuthenticationService } from '@app/shared/services/authentication.service';
import { MarketType } from '@app/domain/model/market-type';
import { PurchaseReceiptReport } from './purchase-receipt-report.model';
import { PurchaseReceiptReportService } from './purchase-receipt-report.service';
import { PurchaseReportCreationEditingComponent } from '../purchase-report-creation-editing/purchase-report-creation-editing.component';
import { ReportingPeriod } from '../reporting-period';
import { ReportType } from '@app/domain/model/report-type';
import { DealerProfile } from '@app/domain/model/dealer-profile';
import _ from "lodash";

@Component({
  selector: 'app-purchase-receipt-report',
  templateUrl: './purchase-receipt-report.component.html',
  styleUrls: ['./purchase-receipt-report.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PurchaseReceiptReportComponent implements OnInit {

  @Input()
  profile: DealerProfile;

  @Input()
  isHistoricalView: boolean;

  isReportModified: boolean;

  @Input()
  marketTypes: Set<MarketType>;

  @Input()
  period: ReportingPeriod;

  purchaseReport: PurchaseReceiptReport;

  @Input()
  reportId: number;

  ReportType: typeof ReportType = ReportType;

  showDeleteWarning: boolean;

  totalReportAmount: Big;

  constructor(private readonly activeModal: NgbActiveModal,
              private readonly modalService: NgbModal,
              private authenticator: AuthenticationService,
              private readonly purchaseReceiptReportService: PurchaseReceiptReportService) {
  }

  ngOnInit() {
    this.getPurchaseReceiptReport();
  }

  onHistoryClick(historyReportId) {
    const modal = this.modalService.open(
      PurchaseReceiptReportComponent,
      { backdrop: 'static', keyboard: false, size: 'lg', windowClass: 'history-modal' }
    );

    modal.componentInstance.reportId = historyReportId;
    modal.componentInstance.isHistoricalView = true;
    modal.componentInstance.period = this.period;
    modal.componentInstance.marketTypes = this.marketTypes;
  }

  onDeleteClick() {
    if (!this.showDeleteWarning) {
      this.showDeleteWarning = true;

      return;
    }

    this.purchaseReceiptReportService.deletePurchaseReceiptReport(this.reportId).subscribe(() => {
      this.activeModal.close(true);
    });
  }

  onEditClick() {
    const modal = this.modalService.open(
      PurchaseReportCreationEditingComponent,
      { backdrop: 'static', keyboard: false, size: 'lg', windowClass: 'purchase-report',  }
    );
    let purchaseReport: PurchaseReceiptReport = _.cloneDeep(this.purchaseReport);
    purchaseReport.purchases.map((purchase, index) => {
      purchase.pricePerPc = null;
      purchase.pricePerLb = null;
    });
    modal.componentInstance.purchaseReport = purchaseReport;
    modal.componentInstance.period = this.period;
    modal.componentInstance.isCreationMode = false;
    modal.componentInstance.marketTypes = this.marketTypes;
    modal.componentInstance.profile = this.profile;


    modal.result.then((editedReportId: number) => {
      if (!editedReportId) {
        return;
      }

      this.reportId = editedReportId;
      this.getPurchaseReceiptReport();
      this.isReportModified = true;
    });
  }

  onPrintClick() {
    this.purchaseReceiptReportService.downloadPurchaseReceiptPdf(this.reportId);
  }

  onCancelClick() {
    this.activeModal.close(this.isReportModified);
  }

  private getPurchaseReceiptReport() {
    this.purchaseReceiptReportService
      .getPurchaseReceiptReport(this.reportId)
      .subscribe((purchaseReport: PurchaseReceiptReport) => {
        this.purchaseReport = purchaseReport;
        this.countTotalReportAmountFromDB();
      },
      (error: Error) =>{
        this.activeModal.close(false);
        throw error;
      });
  }



  private countTotalReportAmountFromDB() {
    this.totalReportAmount = this.purchaseReport.purchases
      .map((purchase) => Big(purchase.value))
      .reduce((totalAmount: Big, purchaseValue: Big) => totalAmount.add(purchaseValue), Big(0));

    if (!this.purchaseReport.amendments.length) {
      return;
    }

    this.totalReportAmount = this.totalReportAmount.add(this.purchaseReport.amendments
      .filter(amendment => !isNaN(amendment.amount))
      .map(amendment => Big(amendment.amount))
      .reduce((totalAmount: Big, amendmentAmount: Big) => totalAmount.add(amendmentAmount), Big(0)));
  }

  isLbsBased(): boolean {
    const reportType = this.purchaseReport.reportType;
    const purchases = this.purchaseReport.purchases;
    return purchases.some((purchase, index, $) => {
      return purchase.isLbsBased(reportType);
    });
  }

  isPcsBased(): boolean {
    const reportType = this.purchaseReport.reportType;
    const purchases = this.purchaseReport.purchases;
    return purchases.some((purchase, index, $) => {
      return purchase.isPcsBased(reportType);
    });
  }

  isConditionNeeded() {
    const reportType = this.purchaseReport.reportType;
    return (ReportType.P === reportType);
  }
}
